// Dialogue Dispatch System
// Handles event dispatching and effects execution for the pure reducer system

import { 
  DialogueEvent, 
  DialogueEffect, 
  DialogueContext, 
  ReducerResult 
} from './types';
import { reduceConversation } from './conversationReducer';
import { ConversationDialogueState } from '../DialogueManagementConversation/ConversationDialogueBlueprint';
import { EffectBus } from './EffectBus';
import { UIEffectsHandler } from './UIEffectsHandler';
import { ServiceEffectsHandler } from './ServiceEffectsHandler';
import { EntryActionsHandler } from './EntryActionsHandler';
import { Lang } from '../../../../i18n';

// Dispatch system state
interface DispatchState {
  conversationState: ConversationDialogueState;
  context: DialogueContext;
}

// Main dispatch system - only queues events, calls reducer, updates state/context
export class DialogueDispatcher {
  private state: DispatchState;
  private effectBus: EffectBus;
  private conversation: any;

  constructor(
    initialState: ConversationDialogueState,
    initialContext: DialogueContext,
    conversation: any,
    triggerConversationUpdate: () => void,
    scrollToBottom: () => void,
    injectedEffectBus?: EffectBus,
    onNewConversationRequested?: () => void,
    currentUser?: string,
    onTaskCompleted?: () => void // Optional callback when a task completes
  ) {
    this.state = {
      conversationState: initialState,
      context: initialContext
    };
    this.conversation = conversation;

    if (injectedEffectBus) {
      this.effectBus = injectedEffectBus;
    } else {
      // Create effect handlers
      const uiHandler = new UIEffectsHandler(
        conversation,
        triggerConversationUpdate,
        scrollToBottom,
        (event: DialogueEvent) => this.dispatch(event),
        () => this.state.context.language as Lang,
        onTaskCompleted // Pass callback to UIEffectsHandler
      );
      
      const serviceHandler = new ServiceEffectsHandler(
        (event: DialogueEvent) => this.dispatch(event),
        conversation,
        () => this.state.context.language as Lang
      );
      
      const entryActionsHandler = new EntryActionsHandler(
        conversation,
        triggerConversationUpdate,
        () => this.state.context.language as Lang,
        onNewConversationRequested,
        currentUser,
        (event: DialogueEvent) => this.dispatch(event)
      );
      
      // Create effect bus with handlers
      this.effectBus = new EffectBus([uiHandler, serviceHandler, entryActionsHandler]);
    }
  }

  async dispatch(event: DialogueEvent): Promise<void> {
    try {
      const addr =
        typeof this.conversation?.assignementAddress === 'string'
          ? this.conversation.assignementAddress.trim()
          : '';
      const contextIn: DialogueContext = {
        ...this.state.context,
        assignementDefaultLocation: addr || undefined,
      };
      const result: ReducerResult = reduceConversation(this.state.conversationState, contextIn, event);

      // Update state - result.nextContext is already a complete context object
      const previousState = this.state.conversationState;
      this.state.conversationState = result.nextState;
      this.state.context = result.nextContext;

      // Hand effects off to EffectBus
      console.log('🔷 [DialogueDispatcher] Reducer returned', result.effects.length, 'effects:', result.effects.map((e: any) => e.type));
      await this.effectBus.runEffects(result.effects);
      console.log('🔷 [DialogueDispatcher] All effects processed');
      
    } catch (error) {
      // Don't rethrow - prevent cascade failures
    }
  }

  getCurrentState(): DispatchState {
    return this.state;
  }

  getEffectBus(): EffectBus {
    return this.effectBus;
  }

  updateLanguage(newLanguage: Lang): void {
    this.state.context.language = newLanguage;
  }

  updateState(newState: ConversationDialogueState): void {
    this.state.conversationState = newState;
  }
}
